import { format } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

// The shop's own timezone drives booking times, not the visitor's device - a
// customer in Karachi booking a Texas shop must see Texas hours, not Karachi hours.
// Whichever screen first loads the shop calls setActiveShopTimeZone(); every date
// util below then defaults to it instead of the browser's zone. Mirrors vara-app's
// src/app/utils/timezone.ts so both apps treat shop time the same way.
let activeShopTimeZone: string | undefined;

export const setActiveShopTimeZone = (timeZoneId?: string | null) => {
    if (timeZoneId) activeShopTimeZone = timeZoneId;
};

export const shopTimeZoneFromShop = (
    shop?: { timezone?: { timeZoneId?: string | null } | null } | null
): string | undefined => {
    return shop?.timezone?.timeZoneId || undefined;
};

export const getUserTimezone = (
    shop?: { timezone?: { timeZoneId?: string | null } | null } | null
) => {
    return (
        shopTimeZoneFromShop(shop) ||
        activeShopTimeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );
};

/**
 * Converts a UTC ISO string to a local Date object whose fields (hours, minutes, etc.)
 * match the time as it appears in the shop's timezone.
 */
export const toLocalDate = (utcString: string, timeZoneId?: string) => {
    const timezone = timeZoneId || getUserTimezone();
    const zoned = toZonedTime(utcString, timezone);
    return new Date(
        zoned.getFullYear(),
        zoned.getMonth(),
        zoned.getDate(),
        zoned.getHours(),
        zoned.getMinutes(),
        zoned.getSeconds()
    );
};

/**
 * Formats a UTC ISO string to a shop-local time string.
 * Defaults to "hh:mm a".
 */
export const formatLocalTime = (
    utcString: string,
    formatStr: string = "hh:mm a",
    timeZoneId?: string
) => {
    const timezone = timeZoneId || getUserTimezone();
    return format(toZonedTime(utcString, timezone), formatStr);
};

/**
 * Formats a UTC ISO string to a shop-local date string.
 * Defaults to "yyyy-MM-dd".
 */
export const formatLocalDate = (
    utcString: string,
    formatStr: string = "yyyy-MM-dd",
    timeZoneId?: string
) => {
    const timezone = timeZoneId || getUserTimezone();
    return format(toZonedTime(utcString, timezone), formatStr);
};

/**
 * Converts a Date constructed by toLocalDate() (whose fields represent shop-timezone wall-clock)
 * back to a proper UTC ISO string suitable for API payloads.
 */
export const localDateToUtc = (localDate: Date, timeZoneId?: string): string => {
    const timezone = timeZoneId || getUserTimezone();
    // Treat the local Date's fields as shop-timezone time → convert to true UTC
    return fromZonedTime(localDate, timezone).toISOString();
};
